<template>
  <div class="ov-card">
    <div class="ov-card__head">
      <div class="ov-card__title">Statut du compte</div>
    </div>

    <div class="ov-card__body">
      <!-- Chargement : on n'affiche pas « inactif » tant que le compte n'est pas connu -->
      <div v-if="!isLoaded" class="us-loading">
        <span class="us-loading__line"></span>
        <span class="us-loading__line us-loading__line--short"></span>
      </div>

      <template v-else>
        <div class="us-status" :class="isActive ? 'us-status--active' : 'us-status--inactive'">
          <span class="us-status__icon">
            <v-icon size="20">{{ isActive ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
          </span>
          <div>
            <div class="us-status__title">{{ isActive ? 'Compte actif' : 'En attente d\'activation' }}</div>
            <div class="us-status__text">
              {{
                isActive
                  ? "L'utilisateur peut se connecter et accéder aux fonctionnalités de son rôle."
                  : "L'utilisateur ne peut pas encore se connecter."
              }}
            </div>
          </div>
        </div>

        <template v-if="!isActive">
          <ul class="us-steps">
            <li>Vérifiez les informations et le rôle de l'utilisateur.</li>
            <li>Activez le compte : il pourra se connecter avec son pseudo <strong>{{ account.login }}</strong>.</li>
          </ul>

          <button
            type="button"
            class="ov-btn ov-btn--primary ov-btn--block"
            :disabled="activating"
            @click="confirmActivation"
          >
            <v-progress-circular v-if="activating" indeterminate size="16" width="2"></v-progress-circular>
            <v-icon v-else small>mdi-account-check-outline</v-icon>
            {{ activating ? 'Activation en cours...' : 'Activer le compte' }}
          </button>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_ACTIVATE_USER,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";

export default {
  name: "UserStatus",

  props: {
    account: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      activating: false,

      toActive: {
        data: {
          login: null,
        },
      },
    };
  },

  computed: {
    // La fiche passe un objet vide tant que l'utilisateur n'est pas chargé
    isLoaded() {
      return !!(this.account && this.account.login);
    },

    isActive() {
      return this.account.isActif === true;
    },

    fullName() {
      return [this.account.nom, this.account.prenoms].filter(Boolean).join(" ") || this.account.login;
    },
  },

  methods: {
    async confirmActivation() {
      const result = await this.$swal.fire({
        title: "Activer ce compte ?",
        text: `${this.fullName} pourra se connecter et utiliser les fonctionnalités de son rôle.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#159e72",
        confirmButtonText: "Activer",
        cancelButtonText: "Annuler",
      });
      if (result.isConfirmed) {
        this.toActivedUser();
      }
    },

    // ACTIVER UN UTILISATEUR
    async toActivedUser() {
      if (this.activating) return;
      this.activating = true;
      this.toActive.data.login = this.account.login;
      await axios
        .post(API_ACTIVATE_USER, this.toActive, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200 && response.data.status.code == 800) {
            this.$swal.fire("Activation", response.data.status.message, "success");
            this.$emit("account-activated", this.account.isActif);
          } else if (response.status == 200) {
            this.$swal.fire("Activation", response.data.status.message, "error");
          } else {
            this.$swal.fire("Activation", "Erreur lors de l'activation du compte.", "error");
          }
        })
        .catch((e) => {
          this.$swal.fire("Activation refusée", (e && e.message) || String(e), "error");
        })
        .finally(() => {
          this.toActive.data.login = null;
          this.activating = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/admin/ui";

.us-status {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;

  &--active {
    background: rgba(21, 158, 114, 0.08);

    .us-status__icon,
    .us-status__title {
      color: $ov-green-dark;
    }
  }

  &--inactive {
    background: #fffbeb;

    .us-status__icon,
    .us-status__title {
      color: #b45309;
    }
  }
}

.us-status__icon {
  flex-shrink: 0;
  display: inline-flex;
  margin-top: 1px;

  .v-icon {
    color: inherit !important;
  }
}

.us-status__title {
  font-size: 14px;
  font-weight: 800;
}

.us-status__text {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.5;
  color: $ov-muted;
}

.us-steps {
  margin: 16px 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.6;
  color: $ov-muted;

  li + li {
    margin-top: 4px;
  }

  strong {
    color: $ov-ink;
  }
}

/* SQUELETTE DE CHARGEMENT */
.us-loading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.us-loading__line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: us-shimmer 1.4s ease-in-out infinite;

  &--short {
    width: 60%;
  }
}

@keyframes us-shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .us-loading__line {
    animation: none;
  }
}
</style>
